"use client";

import * as React from "react";
import { StaticImageData } from "next/image";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import LinkWithLang, {
  type LinkRouteDefaults,
} from "@stadium/shared/src/components/link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { B_04, BANNER_DEFAULTS } from "@stadium/shared/src/helpers/plasmic/static-slices";
import { type LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface B04Props {
  // Content
  eyebrow?: string;
  heading?: string;
  description?: string;

  // CTA
  buttonText?: string;
  buttonLinkType?: string;
  buttonOpenInNewTab?: boolean;
  buttonOtherUrl?: string;
  buttonCategorySlug?: string;

  // Background Image
  mobileImg?: string | StaticImageData;
  desktopImg?: string | StaticImageData;

  imageAlt?: string;
  overlayOpacity?: number;
  className?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

const B04: React.FC<Readonly<B04Props>> = ({
  eyebrow = BANNER_DEFAULTS[B_04].eyebrow,
  heading = BANNER_DEFAULTS[B_04].heading,
  description = BANNER_DEFAULTS[B_04].description,
  buttonText = BANNER_DEFAULTS[B_04].buttonText,
  buttonLinkType = BANNER_DEFAULTS[B_04].buttonLinkType,
  buttonOpenInNewTab = false,
  buttonOtherUrl,
  buttonCategorySlug,
  mobileImg = BANNER_DEFAULTS[B_04].mobileImg,
  desktopImg = BANNER_DEFAULTS[B_04].desktopImg,
  imageAlt = BANNER_DEFAULTS[B_04].imageAlt,
  overlayOpacity = BANNER_DEFAULTS[B_04].overlayOpacity,
  className = "",
  linkRouteDefaults,
}) => {
  const { isMobileDevice, isTabletDevice } = React.useContext(CurrentDeviceContext);

  return (
    <section className={`${styles.sectionWrapper} ${className}`}>
      <div className={styles.b04container}>
        {/* Background Image Container */}
        <div className={styles.backgroundContainer}>
          {/* Background Image */}
          <div className={styles.imageWrapper}>
            {isMobileDevice || isTabletDevice ? (
              <Multimedia
                type="image"
                src={mobileImg}
                alt={imageAlt}
                className={styles.mobileImage}
              />
            ) : (
              <Multimedia
                type="image"
                src={desktopImg}
                alt={imageAlt}
                className={styles.desktopImage}
              />
            )}
          </div>

          <div
            className={styles.overlay}
            style={{
              opacity: overlayOpacity,
            }}
          />

          {/* Content Overlay */}
          <div className={styles.contentOverlay}>
            <div className={styles.contentInner}>
              <div className={styles.contentBox}>
                {/* Eyebrow Text */}
                {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}

                {/* Heading */}
                <h2 className={styles.heading}>{heading}</h2>

                {/* Description */}
                {description && <p className={styles.description}>{description}</p>}

                {/* CTA Button */}
                <div className={styles.buttonWrapper}>
                  {buttonText && buttonLinkType && (
                    <LinkWithLang
                      {...linkRouteDefaults}
                      type={buttonLinkType as LinkWithLangType}
                      data={{
                        code: buttonCategorySlug,
                        url: buttonOtherUrl,
                      }}
                      target={buttonOpenInNewTab ? "_blank" : "_self"}
                      showExternalLinkIcon={buttonOpenInNewTab}
                      className={styles.lightBtn}
                    >
                      {buttonText}
                    </LinkWithLang>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B04;
